import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import { CARD_WALL_RADIUS, CARD_WALL_TOUCH } from "@/shared/ui/cardWall";
import type { PosterTemplateBrief } from "../../../api/posterTemplateService";

import { TemplatePreview } from "./TemplatePreview";

interface Props {
  tpl: PosterTemplateBrief;
  onApply: (tpl: PosterTemplateBrief) => void;
}

export function TemplateCard({ tpl, onApply }: Props) {
  const { t } = useTranslation("poster");
  return (
    <div
      data-template-card={tpl.id}
      className={cn(
        // 形状锁单源(G1):原来写死 rounded-2xl(恒 16px),三皮肤的圆角语言全被它压平
        "group relative overflow-hidden border border-border bg-card",
        CARD_WALL_RADIUS,
        "shadow-lg shadow-black/40 transition-all duration-300",
        "hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/20"
      )}
    >
      <TemplatePreview tpl={tpl} size="card" />

      {/* V2 (2026-05-11) — IMAGE 模板悬浮层只用半透明暗色,不再用 design token 渐变 */}
      <div
        className={cn(
          "pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 opacity-0 transition-opacity duration-200",
          "bg-black/60 backdrop-blur-sm",
          // ★键盘要与鼠标等价:只有 group-hover 时,Tab 到「套用模板」会把焦点丢进
          //   opacity:0 的遮罩里 —— 焦点在一个看不见的按钮上(门 H-D 实测)。
          "group-hover:pointer-events-auto group-hover:opacity-100",
          "group-focus-within:pointer-events-auto group-focus-within:opacity-100"
        )}
      >
        <Button
          size="sm"
          className={cn(
            "gap-1.5 bg-white px-5 font-semibold text-black shadow-xl hover:bg-white/90",
            CARD_WALL_TOUCH
          )}
          onClick={() => onApply(tpl)}
        >
          <Sparkles className="h-3.5 w-3.5" />
          {t("homeV3.applyTemplate")}
        </Button>
        <span className="text-2xs text-white/80">{"骨架铺进搭建器,内容换成你的"}</span>
      </div>

      <div className="flex items-center justify-between gap-2 px-3 py-2.5">
        <div className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
          {tpl.name}
        </div>
        {/* ★0926:删「{N}+」使用次数角标 —— 后端从不累加 recent_uses(只在建模板时置 0),
            角标要么不出、要么出的是 seed 常量,不是真实使用次数。 */}
      </div>
    </div>
  );
}
